"use client"

import { ServiceUser, ServiceUserVerificationResponse } from "@/validated-servertypes";
import {
    Modal,
    ModalContainer,
    ModalDialog,
    ModalHeader,
    ModalBody,
    Input,
    ModalFooter,
    Button,
    TextField,
    Label,
} from "@heroui/react";
import { useState } from "react";
import { useUser } from "./dpswr";
import { toast } from "react-toastify";
import { ClientPostFetcherErrorResponse, clientPostAuthenticatedFetcher } from "./dpfetcher";
import { dpTranslateError } from "./dperror";
import { useSWRConfig } from "swr";
import { useRouter } from "next/navigation";
import { useStytch } from "@stytch/nextjs";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { CommonErrorStandards, CommonErrorType } from "@/commontypes";

export interface DeleteAccountModalProps {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    onOpenChange: () => void;
    locale: string;
    isControlled?: boolean;
    getButtonProps?: (props?: unknown) => unknown;
    getDisclosureProps?: (props?: unknown) => unknown;
}

export default function DeleteAccountModal ({ isOpen, onClose, onOpenChange, locale }: DeleteAccountModalProps) {

    const [deleteVerificationCode, setDeleteVerificationCode] = useState<string>( "" );
    const [isDeleting, setIsDeleting] = useState<boolean>( false );
    const {user, isLoading } = useUser();
    const stytchClient = useStytch();

    const router = useRouter();
    const {mutate} = useSWRConfig();

    const confirmDeleteEnabled = isOpen && deleteVerificationCode !== "" && !isDeleting;

    if ( ( !user || isLoading ) && isOpen ) {
        onClose();

        return(
            <>
            </>
        )
    }

    return(
        <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        >
            <ModalContainer>
                <ModalDialog>
                    <ModalHeader
                        className="flex flex-col gap-1"
                    >
                        <Trans>Delete Donor Profile</Trans>
                    </ModalHeader>
                    <ModalBody>
                        <p>
                            <span>{t`An email with a 6 digit verification code has been sent to`}</span>&nbsp;<span><strong>{user?.principal}</strong>.</span>
                        </p>
                        <p className="mb-2">
                            <Trans>To delete your donor profile, enter the verification code and select Confirm delete.</Trans>
                        </p>
                        <TextField>
                            <Label>{t`Verification code`}</Label>
                            <Input
                                name="verification-code"
                                placeholder={t`Verification code`}
                                value={deleteVerificationCode}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDeleteVerificationCode( event.target.value ) }
                            />
                        </TextField>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="outline" onPress={onClose}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button
                            variant="danger"
                            isDisabled={!confirmDeleteEnabled}
                            // NOTENOTE: Need to use await
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            onPress={
                                async () => {
                                    setIsDeleting( true );

                                    const verificationResponse: ServiceUserVerificationResponse = {
                                        principalResponse: {
                                            email: user?.principal as string,
                                            code: deleteVerificationCode
                                        }
                                    }

                                    let statusCode: number | undefined = 200;

                                    try {
                                        const result: ServiceUser | ClientPostFetcherErrorResponse = await clientPostAuthenticatedFetcher(
                                            "deleteCurrentUser",
                                            verificationResponse
                                        ) as ServiceUser;

                                        const spResult = ServiceUser.safeParse( result );
                                        if ( spResult.success === false ) {
                                            statusCode = ( result as unknown as ClientPostFetcherErrorResponse ).statusCode;
                                        }
                                    }
                                    catch {
                                        statusCode = CommonErrorStandards[CommonErrorType.UnknownInternalServiceException].code
                                    }

                                    if ( statusCode === 200 ) {
                                        try {
                                            await stytchClient.session.revoke();
                                        }
                                        catch {
                                            // BUGBUG: Need debug log warning story (DataDog etc.)
                                        }

                                        await mutate( ["getCurrentUser", undefined] );
                                        router.push( "/" + locale.split("-")[0] );
                                        onClose();
                                    }
                                    else {
                                        const error: string = dpTranslateError(
                                            statusCode,
                                            t`Your profile could not be deleted`,
                                            t`Please ensure you are properly signed in and using the correct verification code`
                                        );

                                        toast.error( error );
                                    }

                                    setIsDeleting( false );
                                }
                            }
                        >
                            <Trans>Confirm delete</Trans>
                        </Button>
                    </ModalFooter>
                </ModalDialog>
            </ModalContainer>
        </Modal>

    )
}